import { Observable, firstValueFrom } from 'rxjs';
import { Config } from '../config';
import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Diagnostic } from '@awesome-cordova-plugins/diagnostic/ngx';
import { AndroidPermissions } from '@awesome-cordova-plugins/android-permissions/ngx';
import { OpenNativeSettings } from '@awesome-cordova-plugins/open-native-settings/ngx';
import { LoadingController, Platform, ToastController } from '@ionic/angular';
import {
  ReportReasonsResponseModel,
  StaticPageModel,
} from '../models/user.model';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';

@Injectable({
  providedIn: 'root',
})
export class GeneralService extends HttpService {
  isLoading = false;
  activeLoader: HTMLIonLoadingElement | null = null;
  public cordova = false;
  notificationCounter = 0;

  constructor(
    http: HttpClient,
    private platform: Platform,
    private diagnostic: Diagnostic,
    private translate: TranslateService,
    private loadingCtrl: LoadingController,
    private toastController: ToastController,
    private androidPermissions: AndroidPermissions,
    private openNativeSettings: OpenNativeSettings,
  ) {
    super(http);
    if (this.platform.is('android') || this.platform.is('ios')) {
      this.cordova = true;
    }
  }

  getReportReasons(
    params?: any,
  ): Observable<Array<ReportReasonsResponseModel>> {
    return this.http.get<Array<ReportReasonsResponseModel>>(
      `${Config.apiUrl}/user/report/reasons`,
      {
        params: this.serialize(params),
      },
    );
  }

  getLoggedInUser(user$): any {
    const promise = new Promise((resolve, reject) => {
      user$.subscribe((data) => {
        resolve(data);
      });
    });
    return promise;
  }

  getPageContent(params: { type: string }) {
    return this.http.get<StaticPageModel>(`${Config.apiUrl}/page/detail`, {
      params: this.serialize(params),
    });
  }

  // getComponentByType(address: Address, type: string): AddressComponent {
  //     if (!type) {
  //         return null;
  //     }
  //
  //     if (!address || !address.address_components || address.address_components.length === 0) {
  //         return null;
  //     }
  //
  //     type = type.toLowerCase();
  //
  //     for (const comp of address.address_components) {
  //         if (!comp.types || comp.types.length === 0) {
  //             continue;
  //         }
  //
  //         if (comp.types.findIndex(x => x.toLowerCase() === type) > -1) {
  //             return comp;
  //         }
  //     }
  //
  //     return null;
  // }

  /**********************************************************************/
  /*************************** General Methods **************************/
  /**********************************************************************/

  /**
   * checks for permission on android only
   */
  checkPermission(callback) {
    if (this.cordova && this.platform.is('android')) {
      this.androidPermissions
        .checkPermission(
          this.androidPermissions.PERMISSION.ACCESS_FINE_LOCATION,
        )
        .then(
          (result) => {
            console.log('Has location permission?', result.hasPermission);
            if (!result.hasPermission) {
              this.androidPermissions
                .requestPermission(
                  this.androidPermissions.PERMISSION.ACCESS_FINE_LOCATION,
                )
                .then((permResp) => {
                  console.log('req location perm response: ', permResp);
                  if (permResp.hasPermission) {
                    callback(true);
                  } else {
                    this.translate
                      .get(['LocationRejected'])
                      .subscribe((messages) => {
                        this.openLocationSettings(messages.LocationRejected);
                        callback(false);
                      });
                  }
                });
            } else {
              callback(true);
            }
          },
          (err) =>
            this.androidPermissions.requestPermission(
              this.androidPermissions.PERMISSION.ACCESS_FINE_LOCATION,
            ),
        );
    } else {
      callback(true);
    }
  }

  /**
   * checks for permission on android only
   */
  checkCameraPermission(callback: any) {
    if (this.cordova && this.platform.is('android')) {
      this.androidPermissions
        .checkPermission(this.androidPermissions.PERMISSION.CAMERA)
        .then(
          (result) => {
            console.log('Has location permission?', result.hasPermission);
            if (!result.hasPermission) {
              this.androidPermissions
                .requestPermission(this.androidPermissions.PERMISSION.CAMERA)
                .then((permResp) => {
                  console.log('req location perm response: ', permResp);
                  if (permResp.hasPermission) {
                    callback(true);
                  } else {
                    callback(false);
                  }
                });
            } else {
              callback(true);
            }
          },
          (err) =>
            this.androidPermissions.requestPermission(
              this.androidPermissions.PERMISSION.CAMERA,
            ),
        );
    } else {
      callback(true);
    }
  }
  async showToast(message) {
    const toast = await this.toastController.create({
      message,
      duration: 5000,
      position: 'top',
      buttons: [
        {
          text: this.translate.instant(marker('Buttons.Ok')),
          role: 'cancel',
          handler: () => {
            console.log('Cancel clicked');
          },
        },
      ],
    });
    toast.present();
  }

  gpsStatus() {
    if (this.cordova) {
      return this.diagnostic.isLocationEnabled();
    }
  }

  async showLoader() {
    this.isLoading = true;
    try {
      const messages = await firstValueFrom(this.translate.get(['Loading']));
      this.activeLoader = await this.loadingCtrl.create({
        message: messages['Loading'],
      });
      await this.activeLoader.present();
      if (!this.isLoading) {
        // hideLoader() werd al aangeroepen terwijl we nog aan het opbouwen waren
        await this.hideLoader();
      }
    } catch (error) {
      console.error('showLoader failed', error);
    }
  }

  async hideLoader() {
    this.isLoading = false;
    if (this.activeLoader) {
      const loader = this.activeLoader;
      this.activeLoader = null;
      try {
        await loader.dismiss();
      } catch (error) {
        console.warn('hideLoader dismiss failed', error);
      }
    }
  }

  async openLocationSettings(message) {
    const toast = await this.toastController.create({
      message,
      color: 'light',
      duration: 5000,
      position: 'top',
      buttons: [
        {
          text: this.translate.instant(marker('Buttons.Settings')),
          handler: () => {
            this.openNativeSettings.open('location');
          },
        },
      ],
    });
    await toast.present();

    const { role } = await toast.onDidDismiss();
    console.log('onDidDismiss resolved with role', role);
  }
}
